import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  BookOpen,
  FileText,
  Plus,
  Trash2,
  Edit,
  ChevronUp,
  ChevronDown,
  PanelRightClose,
  PanelRightOpen,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { IconButton } from '@/components/atoms/IconButton';
import { Input } from '@/components/atoms/Input';
import { Select } from '@/components/atoms/Select';
import { Badge } from '@/components/atoms/Badge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/molecules/Card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/molecules/Tabs';
import {
  useBackofficeConversationPractice,
  useSaveConversationPractice,
  useConversationContent,
  useConversationActivities,
} from '@/hooks/useConversationData';
import { conversationBackofficeRoutes } from '@/constants/conversationBackofficeRoutes';
import { useCategoryLabels } from '@/hooks/useCategoryLabels';
import type { EnglishLevel } from '@/types/story.types';
import type {
  ConversationPracticeStatus,
  ConversationPracticeFormData,
} from '@/types/conversation.types';
import type { BackgroundImageVersion } from '@/types/backoffice.types';
import { ApiError } from '@/services/api/client';
import * as conversationsApi from '@/services/api/conversations.api';
import styles from './ConversationEditPage.module.scss';
import { ConversationSituationPanel } from './ConversationSituationPanel';

const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  multiple_choice: 'Opción múltiple',
  fill_in_blank: 'Completar huecos',
  fill_in_blank_tap: 'Completar huecos (tap)',
  true_false: 'Verdadero/Falso',
  matching: 'Emparejar',
  ordering: 'Ordenar',
  open_ended: 'Respuesta abierta',
};

/** Mismo patrón que ChallengeEditPage: URL manual + Generar imagen (IA); visible en la zona principal de conversación. */
function ConversationPracticeBackgroundImageCard({
  backgroundImage,
  setBackgroundImage,
  backgroundImageHistory,
  imagePreviewError,
  setImagePreviewError,
  isGeneratingBackgroundImage,
  onGenerate,
}: {
  backgroundImage: string;
  setBackgroundImage: (url: string) => void;
  backgroundImageHistory: BackgroundImageVersion[];
  imagePreviewError: boolean;
  setImagePreviewError: (v: boolean) => void;
  isGeneratingBackgroundImage: boolean;
  onGenerate: () => void | Promise<void>;
}) {
  return (
    <Card className={styles.resourceEdit__conversationImageCard}>
      <CardHeader>
        <CardTitle>Imagen de fondo de la práctica</CardTitle>
        <CardDescription>
          Igual que en las historias: la IA construye la escena con el{' '}
          <strong>título</strong> y el <strong>contexto narrativo</strong> guardados en la situación (
          <code>situation.title</code> y <code>situation.context</code>). Guarda la conversación antes si acabas de
          generarla.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className={styles.resourceEdit__formGroup}>
          <label htmlFor="conversationBackgroundImage-main">URL de la imagen</label>
          <div className={styles.resourceEdit__inlineInputActions}>
            <Input
              id="conversationBackgroundImage-main"
              name="conversationBackgroundImage-main"
              type="url"
              placeholder="https://..."
              value={backgroundImage}
              onChange={(e) => setBackgroundImage(e.target.value)}
            />
            <Button
              type="button"
              variant="secondary"
              leftIcon={
                <RefreshCw
                  size={16}
                  className={isGeneratingBackgroundImage ? styles.resourceEdit__spin : undefined}
                />
              }
              onClick={() => void onGenerate()}
              disabled={isGeneratingBackgroundImage}
            >
              {isGeneratingBackgroundImage
                ? 'Generando…'
                : backgroundImage.trim()
                  ? 'Regenerar imagen (IA)'
                  : 'Generar imagen (IA)'}
            </Button>
          </div>
          {backgroundImage.trim() ? (
            <div className={styles.resourceEdit__imagePreview}>
              {imagePreviewError ? (
                <div className={styles.resourceEdit__imagePreviewError}>
                  No se ha podido cargar la imagen desde esta URL.
                </div>
              ) : (
                <img
                  src={backgroundImage}
                  alt="Vista previa de la imagen de fondo"
                  onError={() => setImagePreviewError(true)}
                />
              )}
            </div>
          ) : null}
          {backgroundImageHistory.length > 0 ? (
            <div className={styles.resourceEdit__imageGallery}>
              <span className={styles.resourceEdit__imageGalleryLabel}>Versiones generadas</span>
              <div className={styles.resourceEdit__imageGalleryList}>
                {backgroundImageHistory.map((entry) => {
                  const isActive = entry.url === backgroundImage;
                  return (
                    <button
                      key={`${entry.version}-${entry.url}`}
                      type="button"
                      className={[
                        styles.resourceEdit__imageGalleryItem,
                        isActive ? styles['resourceEdit__imageGalleryItem--active'] : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => setBackgroundImage(entry.url)}
                      aria-label={`Usar versión V${entry.version}`}
                      aria-pressed={isActive}
                    >
                      <img src={entry.url} alt={`Versión V${entry.version}`} />
                      <span className={styles.resourceEdit__imageGalleryBadge}>V{entry.version}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

export function ConversationEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id;

  const tabFromUrl = searchParams.get('tab') as 'resources' | 'activities' | null;
  const validTab =
    tabFromUrl && ['resources', 'activities'].includes(tabFromUrl) ? tabFromUrl : 'resources';

  const { resource, isLoading, refetchResource } = useBackofficeConversationPractice(
    isNew ? undefined : id
  );
  const { saveConversationPractice, isSaving } = useSaveConversationPractice();
  const { content, refetch } = useConversationContent(isNew ? undefined : id);
  const { activities, deleteActivity, reorderActivity } = useConversationActivities(
    isNew ? undefined : id
  );

  const { categoryOptions, labelFor } = useCategoryLabels();

  // Form state
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [level, setLevel] = useState<EnglishLevel>('B1');
  const [category, setCategory] = useState<string>('');
  const [status, setStatus] = useState<ConversationPracticeStatus>('draft');
  const [estimatedTime, setEstimatedTime] = useState<number>(30);
  const [xpReward, setXpReward] = useState<number>(100);
  const [backgroundImage, setBackgroundImage] = useState<string>('');
  const [backgroundImageHistory, setBackgroundImageHistory] = useState<BackgroundImageVersion[]>([]);
  const [imagePreviewError, setImagePreviewError] = useState<boolean>(false);
  const [isGeneratingBackgroundImage, setIsGeneratingBackgroundImage] = useState(false);

  // Update form state when conversation loads (edit mode)
  useEffect(() => {
    if (resource && !isNew) {
      setTitle(resource.title || '');
      setDescription(resource.description || '');
      setLevel(resource.level || 'B1');
      setCategory(resource.category ?? '');
      setStatus(resource.status || 'draft');
      setEstimatedTime(resource.estimatedTime || 30);
      setXpReward(resource.xpReward || 100);
      setBackgroundImage(resource.backgroundImage?.trim() ? resource.backgroundImage : '');
      setBackgroundImageHistory(resource.backgroundImageHistory ?? []);
    }
  }, [resource, isNew]);

  useEffect(() => {
    setImagePreviewError(false);
  }, [backgroundImage]);

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Por favor, introduce un título para la conversación');
      return;
    }

    try {
      const payload: ConversationPracticeFormData = {
        title: title.trim(),
        description: description.trim(),
        level,
        category: category.trim() || null,
        status,
        estimatedTime,
        xpReward,
        ...(backgroundImage.trim() ? { backgroundImage: backgroundImage.trim() } : {}),
      };
      const result = await saveConversationPractice(isNew ? null : id, payload);
      if (isNew && result) {
        navigate(conversationBackofficeRoutes.edit(result.id), { replace: true });
      } else {
        alert('¡Conversación guardada correctamente!');
      }
    } catch (error) {
      console.error('Failed to save conversation:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      alert(`Error al guardar la conversación: ${errorMessage}`);
    }
  };

  const handleGenerateBackgroundImage = async () => {
    if (!id || isNew) return;
    try {
      setIsGeneratingBackgroundImage(true);
      const { imageUrl, history } =
        await conversationsApi.generateConversationPracticeBackgroundImage(id);
      setBackgroundImage(imageUrl);
      setBackgroundImageHistory(history);
      await refetchResource();
      alert('Imagen generada correctamente');
    } catch (err) {
      console.error('Generate conversation background image error:', err);
      let msg = err instanceof Error ? err.message : 'Error al generar la imagen';
      if (err instanceof ApiError && err.data && typeof err.data === 'object' && 'error' in err.data) {
        const detail = err.data as { error?: string; detail?: string };
        msg = [detail.error, detail.detail].filter(Boolean).join(': ') || msg;
      }
      alert(msg);
    } finally {
      setIsGeneratingBackgroundImage(false);
    }
  };

  const handleAddActivity = () => {
    if (!id || isNew) {
      alert('Guarda la conversación primero antes de añadir actividades');
      return;
    }
    navigate(conversationBackofficeRoutes.activityNew(id));
  };

  const handleEditActivity = (activityId: string) => {
    if (!id || isNew) return;
    navigate(conversationBackofficeRoutes.activityEdit(id, activityId));
  };

  const handleDeleteActivity = async (activityId: string) => {
    if (!window.confirm('¿Seguro que quieres eliminar esta actividad?')) return;
    try {
      await deleteActivity(activityId);
    } catch (error) {
      console.error('Error deleting activity:', error);
      alert('Error al eliminar la actividad');
    }
  };

  const handleMoveActivityUp = async (activity: { id: string; order: number }, index: number) => {
    if (index === 0) return;
    try {
      await reorderActivity(activity.id, index - 1);
    } catch (error) {
      console.error('Error reordering activity:', error);
      alert('Error al reordenar la actividad');
    }
  };

  const handleMoveActivityDown = async (activity: { id: string; order: number }, index: number) => {
    if (index === activities.length - 1) return;
    try {
      await reorderActivity(activity.id, index + 1);
    } catch (error) {
      console.error('Error reordering activity:', error);
      alert('Error al reordenar la actividad');
    }
  };

  const [activeTab, setActiveTab] = useState<'resources' | 'activities'>(validTab);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab && ['resources', 'activities'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  {/* eslint-disable-next-line react-hooks/exhaustive-deps */}
  }, [tabFromUrl]);

  if (isLoading && !isNew) {
    return (
      <div className={styles.resourceEdit}>
        <h1>Cargando...</h1>
      </div>
    );
  }

  return (
    <div className={styles.resourceEdit}>
      <Tabs
        className={styles.resourceEdit__tabs}
        value={activeTab}
        onValueChange={(v) => {
          const tab = v as 'resources' | 'activities';
          setActiveTab(tab);
          if (id && !isNew) {
            const url = conversationBackofficeRoutes.edit(id);
            navigate(`${url}?tab=${tab}`, { replace: true });
          }
        }}
      >
        <div className={styles.resourceEdit__toolbar}>
          <div className={styles.resourceEdit__header}>
            <IconButton
              icon={ArrowLeft}
              onClick={() => navigate(conversationBackofficeRoutes.list)}
              aria-label="Volver a Conversaciones"
            />
            <input
              type="text"
              className={styles.resourceEdit__titleInput}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={isNew ? 'Crear conversación' : 'Añadir título'}
              aria-label="Título de la conversación"
            />
            <div className={styles.resourceEdit__headerActions}>
              <IconButton
                icon={Save}
                variant="primary"
                onClick={handleSave}
                disabled={isSaving}
                aria-label={isSaving ? 'Guardando...' : 'Guardar conversación'}
              />
              <IconButton
                icon={isSidebarOpen ? PanelRightClose : PanelRightOpen}
                onClick={() => setIsSidebarOpen((v) => !v)}
                aria-label={isSidebarOpen ? 'Ocultar panel de información' : 'Mostrar panel de información'}
              />
            </div>
          </div>
        </div>

        <div className={styles.resourceEdit__body} data-sidebar-open={isSidebarOpen}>
          <div className={styles.resourceEdit__main}>
            <TabsList className={styles.resourceEdit__tabsList}>
              <TabsTrigger value="resources">
                Contenido
                {!isNew && content.length > 0 && (
                  <span className={styles.resourceEdit__tabBadge}>{content.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="activities">
                Actividades
                {!isNew && activities.length > 0 && (
                  <span className={styles.resourceEdit__tabBadge}>{activities.length}</span>
                )}
              </TabsTrigger>
            </TabsList>

        <TabsContent value="resources">
          {isNew ? (
            <div className={styles.resourceEdit__notice}>
              <BookOpen size={24} />
              <p>
                <strong>Nota:</strong> Guarda esta conversación primero para añadir contenido.
              </p>
            </div>
          ) : (
            <div className={styles.resourceEdit__tabPanel}>
              <div className={styles.resourceEdit__sectionHeader}>
                <div>
                  <h2 className={styles.resourceEdit__sectionTitle}>Conversación interactiva</h2>
                  <p className={styles.resourceEdit__sectionSubtitle}>
                    Genera situaciones con bifurcaciones (5 decisiones) mediante IA; el JSON se valida en el servidor.
                    Arriba tienes la imagen de fondo (IA), igual que en las historias.
                  </p>
                </div>
              </div>
              <div className={styles.resourceEdit__sectionContent}>
                <ConversationPracticeBackgroundImageCard
                  backgroundImage={backgroundImage}
                  setBackgroundImage={setBackgroundImage}
                  backgroundImageHistory={backgroundImageHistory}
                  imagePreviewError={imagePreviewError}
                  setImagePreviewError={setImagePreviewError}
                  isGeneratingBackgroundImage={isGeneratingBackgroundImage}
                  onGenerate={handleGenerateBackgroundImage}
                />
                <ConversationSituationPanel
                  resourceId={id}
                  level={level}
                  content={content}
                  onSaved={() => {
                    void refetch();
                    void refetchResource();
                  }}
                />
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="activities">
          {isNew ? (
            <div className={styles.resourceEdit__notice}>
              <FileText size={24} />
              <p>
                <strong>Nota:</strong> Guarda esta conversación primero para añadir actividades.
              </p>
            </div>
          ) : (
            <div className={styles.resourceEdit__tabPanel}>
              <div className={styles.resourceEdit__sectionHeader}>
                <div>
                  <h2 className={styles.resourceEdit__sectionTitle}>Actividades</h2>
                  <p className={styles.resourceEdit__sectionSubtitle}>
                    Gestiona las actividades y ejercicios de la conversación
                  </p>
                </div>
                <Button variant="secondary" onClick={handleAddActivity}>
                  Añadir actividad
                </Button>
              </div>
              <div className={styles.resourceEdit__sectionContent}>
              {activities.length === 0 ? (
                <div className={styles.resourceEdit__emptyState}>
                  <FileText size={48} className={styles.resourceEdit__emptyIcon} />
                  <h3 className={styles.resourceEdit__emptyTitle}>Sin actividades</h3>
                  <p className={styles.resourceEdit__emptyDescription}>
                    Añade actividades para hacer esta conversación interactiva
                  </p>
                  <Button
                    leftIcon={<Plus />}
                    onClick={handleAddActivity}
                    className={styles.resourceEdit__addButton}
                  >
                    Añadir primera actividad
                  </Button>
                </div>
              ) : (
                <div className={styles.resourceEdit__activitiesList}>
                    {activities.map((activity, index) => (
                    <Card
                      key={activity.id}
                      className={styles.resourceEdit__activityItem}
                      onClick={() => handleEditActivity(activity.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleEditActivity(activity.id);
                        }
                      }}
                    >
                      <CardHeader className={styles.resourceEdit__activityItemHeader}>
                        <div className={styles.resourceEdit__activityItemInfo}>
                          <Badge variant="default" className={styles.resourceEdit__cardBadge}>{ACTIVITY_TYPE_LABELS[activity.type] ?? activity.type}</Badge>
                          <CardTitle className={styles.resourceEdit__cardItemTitle}>{activity.question}</CardTitle>
                        </div>
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
                        <div
                          className={styles.resourceEdit__activityItemActions}
                          onClick={(e) => e.stopPropagation()}
                          role="group"
                          aria-label="Acciones"
                        >
                          <div className={styles.resourceEdit__activityItemReorder}>
                            <IconButton
                              icon={ChevronUp}
                              onClick={() => handleMoveActivityUp(activity, index)}
                              disabled={index === 0}
                              aria-label="Subir"
                            />
                            <IconButton
                              icon={ChevronDown}
                              onClick={() => handleMoveActivityDown(activity, index)}
                              disabled={index === activities.length - 1}
                              aria-label="Bajar"
                            />
                          </div>
                          <IconButton
                            icon={Edit}
                            onClick={() => handleEditActivity(activity.id)}
                            aria-label="Editar actividad"
                          />
                          <IconButton
                            icon={Trash2}
                            onClick={() => handleDeleteActivity(activity.id)}
                            aria-label="Eliminar actividad"
                          />
                        </div>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              )}
              </div>
            </div>
          )}
        </TabsContent>
          </div>

          {isSidebarOpen && (
          <aside className={styles.resourceEdit__sidebar}>
            <Card>
              <CardHeader>
                <CardTitle>Información de la conversación</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={styles.resourceEdit__form}>
                  <div className={styles.resourceEdit__formGroup}>
                    <label htmlFor="title">Título</label>
                    <Input
                      id="title"
                      name="title"
                      type="text"
                      placeholder="Título de la conversación"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>

                  <div className={styles.resourceEdit__formGroup}>
                    <label htmlFor="description">Descripción</label>
                    <textarea
                      id="description"
                      name="description"
                      placeholder="Descripción de la conversación"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      className={styles.resourceEdit__textarea}
                    />
                  </div>

                  <div className={styles.resourceEdit__formGroup}>
                    <label htmlFor="level">Nivel</label>
                    <Select
                      value={level}
                      onValueChange={(value) => setLevel(value as EnglishLevel)}
                      options={[
                        { label: 'A1', value: 'A1' },
                        { label: 'A2', value: 'A2' },
                        { label: 'B1', value: 'B1' },
                        { label: 'B2', value: 'B2' },
                        { label: 'C1', value: 'C1' },
                        { label: 'C2', value: 'C2' },
                      ]}
                    />
                  </div>

                  <div className={styles.resourceEdit__formGroup}>
                    <label htmlFor="category">Categoría</label>
                    <Select
                      value={category}
                      onValueChange={(value) => setCategory(value)}
                      placeholder="Sin categoría"
                      options={
                        category && !categoryOptions.some((o) => o.value === category)
                          ? [{ label: labelFor(category), value: category }, ...categoryOptions]
                          : categoryOptions
                      }
                    />
                  </div>

                  <div className={styles.resourceEdit__formGroup}>
                    <label htmlFor="status">Estado</label>
                    <Select
                      value={status}
                      onValueChange={(value) => setStatus(value as ConversationPracticeStatus)}
                      options={[
                        { label: 'Borrador', value: 'draft' },
                        { label: 'Publicado', value: 'published' },
                        { label: 'Archivado', value: 'archived' },
                      ]}
                    />
                  </div>

                  <div className={styles.resourceEdit__formGroup}>
                    <label htmlFor="estimatedTime">Tiempo estimado (minutos)</label>
                    <Input
                      id="estimatedTime"
                      name="estimatedTime"
                      type="number"
                      placeholder="30"
                      value={estimatedTime}
                      onChange={(e) => setEstimatedTime(parseInt(e.target.value) || 30)}
                      min="1"
                    />
                  </div>

                  <div className={styles.resourceEdit__formGroup}>
                    <label htmlFor="xpReward">Recompensa XP</label>
                    <Input
                      id="xpReward"
                      name="xpReward"
                      type="number"
                      placeholder="100"
                      value={xpReward}
                      onChange={(e) => setXpReward(parseInt(e.target.value) || 100)}
                      min="1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </aside>
          )}
        </div>
      </Tabs>
    </div>
  );
}

export default ConversationEditPage;
